import { CheckOutlined, CloseCircleFilled, DeleteOutlined, EditOutlined, PlusOutlined } from "@ant-design/icons";
import { Popconfirm, Tooltip, message } from "antd";
import type { TableColumnsType } from "antd";
import { staffApi } from "api/staff.api";
import { todoApi } from "api/todo.api";
import { AppButton, AppTable, AppTableToolbar } from "components/antd-ui";
import {
  ActionButton,
  CustomerCell,
  TableLayout,
  TableLayoutFooter,
  StatusCell,
  TableContent,
  getTableShowingRange,
} from "components/table";
import { debounce } from "lodash";
import { observer } from "mobx-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { userStore } from "store/userStore";
import { ActivityLogLocationState } from "types/activityLog";
import { QueryParam } from "types/query";
import { Staff } from "types/staff";
import { Todo, TodoStatus, todoStatusOptions, todoStatusTrans } from "types/todo";
import { getTitle } from "utils";
import { can } from "utils/auth";
import { formatDate } from "utils/date";
import { fetchCustomerOptions, useRemoteOptions } from "hooks/useRemoteOptions";
import { TodoModal } from "./components/TodoModal";
import "./Todo.scss";

const todoPath = "/todo";

type StaffOption = { label: string; value: number };

export const TodoPage = observer(({ title = "" }) => {
  const [search, setSearch] = useState("");
  const [query, setQuery] = useState<QueryParam>({
    page: 1,
    limit: 50,
    search: "",
  });
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<Todo[]>([]);
  const [total, setTotal] = useState(0);
  const [staffOptions, setStaffOptions] = useState<StaffOption[]>([]);
  const todoModalRef = useRef<TodoModal>(null);
  const selectedStaffRef = useRef<StaffOption>();
  const staffOptionsRef = useRef<StaffOption[]>([]);
  const openedFromLogRef = useRef(false);
  const location = useLocation();
  staffOptionsRef.current = staffOptions;

  const onlyMine = !userStore.info.role?.isAdmin && can(todoPath, "ONLY_MINE");

  useEffect(() => {
    document.title = getTitle(title);
  }, [title]);

  useEffect(() => {
    if (openedFromLogRef.current) {
      return;
    }
    const todo = (location.state as ActivityLogLocationState | null)?.todo;
    if (!todo) {
      return;
    }
    openedFromLogRef.current = true;
    todoModalRef.current?.handleUpdate(todo);
  }, [location.state]);

  const fetchData = useCallback(async (queryParams: QueryParam) => {
    setLoading(true);
    try {
      const res = await todoApi.findAll(queryParams);
      setData(res.data.todos);
      setTotal(res.data.total);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData(query);
  }, [fetchData, query]);

  const handleDelete = useCallback(
    async (todo: Todo) => {
      setLoading(true);
      try {
        await todoApi.delete(todo.id);
        message.success("Xóa việc cần làm thành công");
        await fetchData(query);
      } finally {
        setLoading(false);
      }
    },
    [fetchData, query],
  );

  const debounceSearch = useMemo(
    () =>
      debounce((keyword: string) => {
        setQuery((prev) => ({ ...prev, search: keyword, page: 1 }));
      }, 300),
    [],
  );

  useEffect(() => {
    return () => {
      debounceSearch.cancel();
    };
  }, [debounceSearch]);

  const openEdit = useCallback((todo: Todo) => {
    todoModalRef.current?.handleUpdate(todo);
  }, []);

  const openCreate = useCallback(() => {
    todoModalRef.current?.handleCreate();
  }, []);

  const handleComplete = useCallback(
    async (todo: Todo) => {
      setLoading(true);
      try {
        await todoApi.update(todo.id, {
          todo: {
            name: todo.name,
            deadlineAt: todo.deadlineAt,
            status: "DONE",
            internalNote: todo.internalNote || "",
          },
          staffId: todo.staff?.id,
        });
        message.success("Đã hoàn thành");
        await fetchData(query);
      } finally {
        setLoading(false);
      }
    },
    [fetchData, query],
  );

  const handleSearchChange = useCallback(
    (value: string) => {
      setSearch(value);
      debounceSearch(value);
    },
    [debounceSearch],
  );

  const handleClearSearch = useCallback(() => {
    setSearch("");
    debounceSearch.cancel();
    setQuery((prev) => ({ ...prev, page: 1, search: "" }));
  }, [debounceSearch]);

  const handlePageChange = useCallback(({ limit, page }: { limit: number; page: number }) => {
    setQuery((prev) => ({ ...prev, page, limit }));
  }, []);

  const fetchStaff = useCallback(async (keyword: string) => {
    const res = await staffApi.findAll({ page: 1, limit: 20, search: keyword });
    const staffs: Staff[] = res.data.staffs || [];
    const options = staffs.map((staff) => ({
      label: staff.name || staff.username,
      value: staff.id,
    }));
    const selected = selectedStaffRef.current;
    if (selected && !options.some((option) => option.value === selected.value)) {
      setStaffOptions([selected, ...options]);
      return;
    }
    setStaffOptions(options);
  }, []);

  const debounceSearchStaff = useMemo(
    () =>
      debounce((keyword: string) => {
        fetchStaff(keyword);
      }, 500),
    [fetchStaff],
  );

  useEffect(() => {
    if (onlyMine) {
      return;
    }
    fetchStaff("");
    return () => {
      debounceSearchStaff.cancel();
    };
  }, [debounceSearchStaff, fetchStaff, onlyMine]);

  const handleStaffChange = useCallback((value: string | number | undefined) => {
    const staffId = typeof value === "number" ? value : undefined;
    selectedStaffRef.current = staffId
      ? staffOptionsRef.current.find((option) => option.value === staffId)
      : undefined;
    setQuery((prev) => {
      const next: QueryParam = { ...prev, page: 1 };
      if (staffId) {
        next.staffId = staffId;
      } else {
        delete next.staffId;
      }
      return next;
    });
  }, []);

  const handleStatusChange = useCallback((value: string | number | undefined) => {
    const status = typeof value === "string" && value ? value : undefined;
    setQuery((prev) => {
      const next: QueryParam = { ...prev, page: 1 };
      if (status) {
        next.status = status;
      } else {
        delete next.status;
      }
      return next;
    });
  }, []);

  const customerFilter = useRemoteOptions(fetchCustomerOptions);

  const handleCustomerChange = useCallback(
    (value: string | number | undefined) => {
      const customerId = typeof value === "number" ? value : undefined;
      customerFilter.select(customerId);
      setQuery((prev) => {
        const next: QueryParam = { ...prev, page: 1 };
        if (customerId) {
          next.customerId = customerId;
        } else {
          delete next.customerId;
        }
        return next;
      });
    },
    [customerFilter],
  );

  const filters = useMemo(
    () => [
      {
        key: "status",
        value: (query.status as string | undefined) ?? "",
        options: todoStatusOptions,
        onChange: handleStatusChange,
        placeholder: "Trạng thái",
        allowClear: true,
        width: 160,
      },
      {
        key: "customerId",
        value: (query.customerId as number | undefined) ?? "",
        options: customerFilter.options,
        onChange: handleCustomerChange,
        placeholder: "Khách hàng",
        allowClear: true,
        showSearch: true,
        filterOption: false as const,
        onSearch: customerFilter.onSearch,
        width: 220,
      },
      ...(onlyMine
        ? []
        : [
          {
            key: "staffId",
            value: (query.staffId as number | undefined) ?? "",
            options: staffOptions,
            onChange: handleStaffChange,
            placeholder: "Nhân viên sale",
            allowClear: true,
            showSearch: true,
            filterOption: false as const,
            onSearch: debounceSearchStaff,
            width: 220,
          },
        ]),
    ],
    [
      customerFilter.onSearch,
      customerFilter.options,
      debounceSearchStaff,
      handleCustomerChange,
      handleStaffChange,
      handleStatusChange,
      onlyMine,
      query.customerId,
      query.staffId,
      query.status,
      staffOptions,
    ],
  );

  const refresh = useCallback(() => {
    fetchData(query);
  }, [fetchData, query]);

  const canCreate = can(todoPath, "CREATE");
  const canEdit = can(todoPath, "UPDATE");
  const canDelete = can(todoPath, "DELETE");

  const columns = useMemo<TableColumnsType<Todo>>(
    () => [
      {
        title: "Thời gian (hạn)",
        dataIndex: "deadlineAt",
        key: "deadlineAt",
        width: 140,
        render: (value: number) => <TableContent>{value > 0 ? formatDate(value) : "--"}</TableContent>,
      },
      {
        title: "Khách hàng",
        key: "customer",
        width: 220,
        render: (_, record) => <CustomerCell customer={record.customer} />,
      },
      {
        title: "Tên",
        dataIndex: "name",
        key: "name",
        render: (value) => <TableContent>{value || "--"}</TableContent>,
      },
      {
        title: "Ghi chú nội bộ",
        dataIndex: "internalNote",
        key: "internalNote",
        render: (value) => <TableContent>{value || "--"}</TableContent>,
      },
      {
        title: "Trạng thái",
        dataIndex: "status",
        key: "status",
        width: 140,
        render: (value: TodoStatus) => <StatusCell value={value} trans={todoStatusTrans} />,
      },
      {
        title: "Nhân viên",
        key: "staff",
        width: 160,
        render: (_, record) => <TableContent>{record.staff?.name || "--"}</TableContent>,
      },
      ...(canEdit || canDelete
        ? [
          {
            title: "Thao tác",
            key: "action",
            width: 220,
            align: "center" as const,
            render: (_: unknown, record: Todo) => (
              <div className="flex gap-2 justify-center items-center">
                {canEdit && record.status !== "DONE" && (
                  <ActionButton className="todoCompleteButton" icon={<CheckOutlined />} onClick={() => handleComplete(record)}>
                    Hoàn thành
                  </ActionButton>
                )}
                {canEdit && (
                  <Tooltip title={record.status === "DONE" ? "Xem" : "Cập nhật"}>
                    <ActionButton
                      aria-label={record.status === "DONE" ? "Xem" : "Cập nhật"}
                      onClick={() => openEdit(record)}
                    >
                      <EditOutlined style={{ color: "#6c84e5" }} />
                    </ActionButton>
                  </Tooltip>
                )}
                {canDelete && (
                  <Tooltip title="Xóa">
                    <Popconfirm
                      cancelText="Không"
                      okText="Xóa"
                      onConfirm={() => handleDelete(record)}
                      title="Xác nhận xóa?"
                    >
                      <ActionButton aria-label="Xóa">
                        <DeleteOutlined style={{ color: "#ef4444" }} />
                      </ActionButton>
                    </Popconfirm>
                  </Tooltip>
                )}
              </div>
            ),
          },
        ]
        : []),
    ],
    [canDelete, canEdit, handleComplete, handleDelete, openEdit],
  );

  const { showingFrom, showingTo } = getTableShowingRange(query.page, query.limit, total);

  return (
    <div>
      <TableLayout
        filter={
          <AppTableToolbar
            search={{
              value: search,
              width: 320,
              placeholder: "Tìm theo tên",
              onChange: handleSearchChange,
              inputProps: {
                allowClear: {
                  clearIcon: <CloseCircleFilled onClick={handleClearSearch} />,
                },
              },
            }}
            leftActions={
              canCreate && (
                <AppButton tone="primary" icon={<PlusOutlined />} onClick={openCreate}>
                  Tạo mới
                </AppButton>
              )
            }
            filterLabel={null}
            filters={filters}
          />
        }
        table={
          <AppTable<Todo>
            rowKey="id"
            loading={loading}
            pagination={false}
            dataSource={data}
            columns={columns}
          />
        }
        footer={
          <TableLayoutFooter
            showingFrom={showingFrom}
            showingTo={showingTo}
            total={total}
            currentPage={query.page}
            pageSize={query.limit}
            onChange={handlePageChange}
          />
        }
      />

      <TodoModal ref={todoModalRef} onSubmitOk={refresh} onClose={() => { }} />
    </div>
  );
});
